import { useEffect, useRef, useState } from 'react';

import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';

import { useRecordingControls, useRecordingStore } from '@/entities/audio-recording';
import { clientByIdOptions } from '@/entities/client';
import { useMarkFirstDictation } from '@/entities/practice-member';
import { notesSettingsOptions } from '@/entities/practice-member-settings';
import {
    type generateNotesSchema,
    useGenerateSessionOptions,
    useSessionRecordingStore,
} from '@/entities/session';
import { toNoteSettings } from '@/entities/session-note';
import { useInactivitySignOut } from '@/shared/lib/inactivity-sign-out-context';
import { convertMillisecondsToSeconds } from '@/shared/lib/time-utils';

import { showNoteGeneratingToast } from './show-note-generating-toast';

const SIGN_OUT_CALLBACK_ID = 'recording-sign-out-callback';

export function useGenerateNote(form: UseFormReturn<z.input<typeof generateNotesSchema>>) {
    const { clientId, sessionId, startedAt, status, length, getElapsedTime } = useRecordingStore(
        useShallow((state) => ({
            clientId: state.clientId,
            sessionId: state.recordId,
            startedAt: state.startedAt,
            status: state.status,
            length: state.length,
            getElapsedTime: state.getElapsedTime,
        })),
    );

    const inputMethod = useSessionRecordingStore((state) => state.inputMethod);

    const { finish } = useRecordingControls();
    const { data: notesSettings } = useSuspenseQuery(notesSettingsOptions());
    const generateSessionMutation = useMutation(useGenerateSessionOptions());
    const navigate = useNavigate();
    const markFirstDictation = useMarkFirstDictation();
    const isMobile = useBreakpointDown();
    const queryClient = useQueryClient();
    const [isFinalizing, setIsFinalizing] = useState(false);
    // A plain ref, not the isFinalizing state above: the guard has to be set synchronously, before
    // any await, so a re-entrant call arriving before the next render commits still sees it.
    const isFinalizingRef = useRef(false);

    const generateNote = async () => {
        if (status === 'idle' || !sessionId || !startedAt) {
            console.error('Cannot generate note: no active session');
            return;
        }

        if (length === 'too-short') return;

        if (isFinalizingRef.current) {
            return;
        }
        isFinalizingRef.current = true;

        const approaches = form.getValues('treatmentApproaches');
        const elapsedTimeS = convertMillisecondsToSeconds(getElapsedTime());

        setIsFinalizing(true);

        try {
            await generateSessionMutation.mutateAsync({
                treatmentApproaches: approaches,
                id: sessionId,
                date: startedAt,
                duration: Math.max(1, Math.ceil(elapsedTimeS / 60)),
                clientIds: [clientId ?? ''],
                actions: ['REVIEW_NOTE'],
                inputMethod: inputMethod,
                noteType: notesSettings.value.defaultNoteTemplate,
                settings: toNoteSettings(notesSettings.value),
            });
        } catch (error) {
            console.error('Error generating session note:', error);
            toast.error('Session note generation failed. Please try again.');
            isFinalizingRef.current = false;
            setIsFinalizing(false);
            return;
        }

        // The note has been generated at this point — retrying here would generate a second note
        // for the same session, so the guard stays locked regardless of what happens next.
        try {
            await markFirstDictation();
        } catch (error) {
            // Non-critical bookkeeping — a failure here must not block the toast or navigation
            // below.
            console.error('Error marking first dictation:', error);
        }

        let finishFailed = false;
        try {
            await finish();
        } catch (error) {
            console.error('Error finishing an already-generated session note:', error);
            finishFailed = true;
        }

        if (finishFailed) {
            toast.error('Note generated, but finishing the session failed.');
        } else if (clientId && !isMobile) {
            const client = queryClient.getQueryData(clientByIdOptions(clientId).queryKey);
            showNoteGeneratingToast(client?.name);
        }

        // The note exists and is generating server-side regardless of what happened above, so
        // navigation always follows — stranding the clinician on this screen helps nobody.
        void navigate({
            to: '/dashboard',
            replace: true,
            search: isMobile ? { openSheet: 'sessions/today' } : {},
        });
    };

    const generateNoteRef = useRef(generateNote);
    useEffect(() => {
        generateNoteRef.current = generateNote;
    });

    const { registerCallback, unregisterCallback } = useInactivitySignOut();

    useEffect(() => {
        registerCallback(SIGN_OUT_CALLBACK_ID, () => generateNoteRef.current());

        return () => {
            unregisterCallback(SIGN_OUT_CALLBACK_ID);
        };
    }, [registerCallback, unregisterCallback]);

    return {
        generateNote,
        // Deliberately never reset once generation has started: navigation always follows a
        // generated note, success or failure, and unmounts the modal — so there's nothing left to
        // re-enable, and no case where this would otherwise strand the clinician mid-spinner.
        isLoading: isFinalizing || generateSessionMutation.isPending,
    };
}
